import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/services/api'
import type { CreateReservationGroupPayload } from '@/lib/schemas/create-reservation'
import {
  createReservationErrorMessage,
  invalidateAfterCreateReservation,
  readCreationWarnings,
  toastCreationSuccess,
} from '@/hooks/useCreateReservation'

export type { CreateReservationGroupPayload } from '@/lib/schemas/create-reservation'

/** El que fa servir el frontend de la resposta de `POST /reservations/group` (B1, runbook 7). */
export interface CreatedReservationGroup {
  /** Números de Beds24 de les reserves creades, en l'ordre del grup (la primera és la mestra). */
  beds24Ids: string[]
  /** El grup és creat, però falta alguna cosa (p. ex. el client de facturació en alguna habitació). */
  warnings: string[]
}

function readBeds24Id(item: unknown): string | null {
  if (typeof item !== 'object' || item === null) return null
  const record = item as Record<string, unknown>
  for (const key of ['beds24Id', 'beds24_id']) {
    const value = record[key]
    if (typeof value === 'string' && value.trim() !== '') return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return null
}

/**
 * Llegeix de forma defensiva els números de Beds24 de la resposta del grup: `beds24_ids` o, si no
 * hi és, una llista directa o dins de `reservations`, `created` o `data`, amb `beds24Id` o `beds24_id`.
 * Els `manual-…` hi surten igual; `buildGroupPathFocusedOn` ja els descarta (torna `/calendar-v2`).
 */
export function readGroupBeds24Ids(data: unknown): string[] {
  let list: unknown = data
  if (!Array.isArray(list) && typeof list === 'object' && list !== null) {
    const record = list as Record<string, unknown>
    // Contracte de B1: `beds24_ids` (en l'ordre de `stays`); si no hi és, les reserves retornades.
    const ids = record.beds24_ids
    if (Array.isArray(ids)) {
      const valid = ids.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
      if (valid.length > 0) return valid.map((id) => id.trim())
    }
    list = record.reservations ?? record.created ?? record.data
  }
  if (!Array.isArray(list)) return []
  return list.map(readBeds24Id).filter((id): id is string => id !== null)
}

export function useCreateReservationGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateReservationGroupPayload): Promise<CreatedReservationGroup> => {
      const response = await api.post<unknown>('/reservations/group', payload)
      return { beds24Ids: readGroupBeds24Ids(response.data), warnings: readCreationWarnings(response.data) }
    },
    onSuccess: (created, payload) => {
      invalidateAfterCreateReservation(queryClient)
      toastCreationSuccess(`Grup de ${payload.stays.length} reserves creat correctament`, created.warnings)
    },
    onError: (error: unknown) => {
      toast.error(createReservationErrorMessage(error))
    },
  })
}
